import { faXmark } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { motion } from 'motion/react'
import { useState } from 'react'
import type { UserNotification } from '~/api/queries/user'
import { getNotificationMetas } from '~/components/Layout/Header/Notification/common'
import {
  notificationIcon,
  notificationIconVariants,
  notificationIconWrapper,
  notificationItem,
  notificationItemContent,
  notificationItemDate,
  notificationItemDeleteTrigger,
  notificationItemDeleteTriggerIcon,
  notificationItemDeleteWrapper,
  notificationItemSubtitle,
  notificationItemTitle,
  notificationItemTitleUnread,
  notificationItemUnreadDate,
  notificationTick,
  notificationUnreadTick
} from '~/components/Layout/Header/Notification/Notifications.css'
import { useConfig } from '~/domains/configuration/queries/config'
import { useSafeNavigateToLinkFromLegacy } from '~/hooks/useSafeNavigateToLinkFromLegacy'
import { formatRelativeTime } from '~/utils'

export const Notification = ({
  notification,
  onNotificationRead,
  onNotificationDelete,
  onNotificationOpen
}: {
  notification: UserNotification
  onNotificationRead?: () => void
  onNotificationDelete?: () => void
  onNotificationOpen?: () => void
}) => {
  const { data: legacyUrl = '' } = useConfig('organization.legacy_url')
  const navigateToV3 = useSafeNavigateToLinkFromLegacy()
  const [timeoutId, setTimeoutId] = useState<number | null>(null)
  const { title, subtitle, icon, onClick } = getNotificationMetas(
    notification,
    legacyUrl,
    navigateToV3
  )
  const onNotifClick = () => {
    onNotificationRead?.()
    onNotificationOpen?.()
    onClick()
  }

  return (
    <motion.div
      variants={{
        initial: { opacity: 0, x: -20 },
        hidden: { opacity: 0, x: -20 },
        visible: { opacity: 1, x: 0 }
      }}
      className={notificationItem}
      onMouseEnter={() => {
        setTimeoutId(
          window.setTimeout(() => {
            onNotificationRead?.()
          }, 1000)
        )
      }}
      onMouseLeave={() => {
        if (timeoutId) {
          window.clearTimeout(timeoutId)
        }
      }}
    >
      <div className={notificationIconWrapper} onClick={onNotifClick}>
        {icon && (
          <span
            className={clsx(
              notificationIcon,
              notificationIconVariants[notification.type]
            )}
          >
            <FontAwesomeIcon icon={icon} />
          </span>
        )}
      </div>
      <div onClick={onNotifClick} className={notificationItemContent}>
        <div
          className={clsx(
            notificationItemTitle,
            notification.isRead ? null : notificationItemTitleUnread
          )}
        >
          {title}
        </div>
        <div className={notificationItemSubtitle}>{subtitle}</div>
      </div>
      <div className={notificationItemDeleteWrapper}>
        <div
          className={clsx(
            notificationItemDate,
            notification.isRead ? null : notificationItemUnreadDate
          )}
        >
          {formatRelativeTime(new Date(notification.updatedAt))}
        </div>
        <div className={notificationItemDeleteTrigger}>
          <FontAwesomeIcon
            icon={faXmark}
            onClick={(e) => {
              e.stopPropagation()
              onNotificationDelete?.()
            }}
            className={notificationItemDeleteTriggerIcon}
          />
        </div>
      </div>
      <div className={notificationTick}>
        {!notification.isRead ? (
          <span className={notificationUnreadTick} />
        ) : null}
      </div>
    </motion.div>
  )
}
